import {
  DEFAULT_PREFERRED_TEMPERATURE,
  EditableDisplay,
  FormTextField,
  MAX_PREFERRED_TEMPERATURE,
  MIN_PREFERRED_TEMPERATURE,
  useFormEvents,
} from '@lib/common';
import { FormStatus, guestProfileService, GuestProfilesForms } from '@lib/state';
import React, { useCallback, useMemo } from 'react';
import { ValidationOptions } from 'react-hook-form';
import { InputAdornment, TextFieldProps, Typography, TypographyProps } from '@material-ui/core';

interface NumberInputProps {
  name: string;
  defaultValue?: unknown;
  size: TextFieldProps['size'];
  validationOptions?: ValidationOptions;
  fullWidth?: boolean;
}

const NumberInput: React.FC<NumberInputProps> = ({
  name,
  defaultValue,
  size,
  validationOptions,
  fullWidth,
}) => (
  <FormTextField
    name={name}
    type="number"
    size={size}
    defaultValue={defaultValue}
    validationOptions={validationOptions}
    fullWidth={fullWidth}
    // the thermostat display shows whole degrees only
    inputProps={{ inputMode: 'numeric', step: 1 }}
    InputProps={{ endAdornment: <InputAdornment position="end">°F</InputAdornment> }}
    data-testid="editableDisplayTextBox"
  />
);

const TemperatureDisplay = ({ value, ...props }: { value?: string } & TypographyProps) => (
  <Typography {...props}>{value ? `${value}°F` : 'Not set'}</Typography>
);

/**
 * The value the editor starts at: the guest's preference, or, when none is set, the property's default
 * temperature (70°F when the property has none), kept within the property's min and max.
 */
const getEditingTemperature = (
  preferredTemperature?: number | null,
  defaultTemp?: number,
  minTemp?: number,
  maxTemp?: number
): number => {
  // a property default of 0 means the property has no default set
  const candidate = preferredTemperature ?? (defaultTemp || DEFAULT_PREFERRED_TEMPERATURE);
  const rounded = Math.round(candidate);
  const atLeastMin = minTemp != null ? Math.max(rounded, minTemp) : rounded;
  return maxTemp != null ? Math.min(atLeastMin, maxTemp) : atLeastMin;
};

/**
 * The range My Key accepts: the property's thermostat range, within the 60°F to 80°F a guest can set. A property
 * limit of 0 was never configured, so it counts as unset. When the property's range does not overlap 60°F to 80°F,
 * the full 60°F to 80°F applies; the thermostat clamps the setpoint to its own limits.
 */
const getAllowedRange = (minTemp?: number, maxTemp?: number) => {
  const lowest = Math.max(MIN_PREFERRED_TEMPERATURE, minTemp || MIN_PREFERRED_TEMPERATURE);
  const highest = Math.min(MAX_PREFERRED_TEMPERATURE, maxTemp || MAX_PREFERRED_TEMPERATURE);

  return lowest <= highest
    ? { lowest, highest }
    : { lowest: MIN_PREFERRED_TEMPERATURE, highest: MAX_PREFERRED_TEMPERATURE };
};

interface Props {
  preferredTemperature?: number | null;
  defaultTemp?: number;
  minTemp?: number;
  maxTemp?: number;
  className?: string;
}

export const GuestPreferredTemperature: React.FC<Props> = ({
  preferredTemperature,
  className,
  defaultTemp,
  minTemp,
  maxTemp,
}) => {
  const [{ status, error }] = useFormEvents(GuestProfilesForms.UpdatePreferredTemperature);

  const { lowest, highest } = getAllowedRange(minTemp, maxTemp);

  const validationOptions = useMemo<ValidationOptions>(
    () => ({
      min: { value: lowest, message: `Minimum temperature is ${lowest}°F.` },
      max: { value: highest, message: `Maximum temperature is ${highest}°F.` },
    }),
    [lowest, highest]
  );

  const editingTemperature = getEditingTemperature(
    preferredTemperature,
    defaultTemp,
    lowest,
    highest
  );

  const onSubmit = useCallback((value?: string) => {
    // an empty input clears the preference so the property default applies
    const temperature = value ? parseInt(value, 10) : NaN;
    guestProfileService.updatePreferredTemperature(isNaN(temperature) ? null : temperature);
  }, []);

  return (
    <div className={className}>
      <EditableDisplay
        label="Preferred Room Temp"
        value={preferredTemperature?.toString() ?? ''}
        onSubmit={onSubmit}
        input={NumberInput}
        // start editing within the property's range instead of a blank field
        inputProps={{ defaultValue: editingTemperature.toString() }}
        valueDisplay={TemperatureDisplay}
        validationOptions={validationOptions}
        pending={status === FormStatus.Pending}
        error={error}
        labelVariant="subtitle1"
        valueVariant="subtitle1"
      />
    </div>
  );
};
